import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { graphqlClient } from "@/lib/graphql/client";
import { SIGN_IN_WITH_CODE_MUTATION } from "@/lib/graphql/queries/auth";
import { useAuthStore } from "@/stores/auth-store";
import { decodeJwt } from "@/lib/auth/jwt";
import { validateOAuthState } from "@/lib/auth/oauth-state";
import {
  CONTEST_ONLY_HOME,
  isContestOnlyAdmin,
} from "@/config/contest-only-admins";
import { toast } from "sonner";

interface SignInResponse {
  signInOrSignUpByCode: {
    accessToken: string;
    user: {
      _id: string;
      email: string;
      name: string;
    };
  };
}

export default function CallbackPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const setAuth = useAuthStore((s) => s.setAuth);
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    if (hasProcessedRef.current) {
      return;
    }

    const handleCallback = async () => {
      const params = new URLSearchParams(location.search);
      const code = params.get("code");
      const state = params.get("state");
      const errorParam = params.get("error");

      if (errorParam) {
        setError("인증이 취소되었거나 실패했습니다");
        setTimeout(() => navigate("/login"), 3000);
        return;
      }

      if (!code) {
        setError("인증 코드를 받지 못했습니다");
        setTimeout(() => navigate("/login"), 3000);
        return;
      }

      const validatedState = validateOAuthState(state);
      if (!validatedState) {
        setError("인증 상태 검증에 실패했습니다");
        setTimeout(() => navigate("/login"), 3000);
        return;
      }

      hasProcessedRef.current = true;

      try {
        const response = await graphqlClient.request<SignInResponse>(
          SIGN_IN_WITH_CODE_MUTATION,
          {
            code,
            provider: "google",
            isAdmin: true,
          },
        );

        const { accessToken, user } = response.signInOrSignUpByCode;

        const decoded = decodeJwt(accessToken);
        const isAdmin = decoded?.data?.isAdmin === true;

        if (!isAdmin) {
          toast.error("관리자 권한이 필요합니다");
          setTimeout(() => navigate("/unauthorized"), 3000);
          return;
        }

        setAuth(accessToken, { ...user, isAdmin });

        toast.success(`${user.name}님, 환영합니다!`);

        // 공모전 전용 계정은 저장된 redirectTo(구독 관리 등)를 무시하고
        // 공모전 페이지로 보낸다 — 사이드바에 없는 페이지로 랜딩하는 것 방지.
        navigate(
          isContestOnlyAdmin(user.email)
            ? CONTEST_ONLY_HOME
            : validatedState.redirectTo,
        );
      } catch {
        setError("인증에 실패했습니다. 다시 시도해주세요.");
        setTimeout(() => navigate("/login"), 3000);
      }
    };

    handleCallback();
  }, [location.search, navigate, setAuth]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">{error}</h2>
          <p className="mt-2 text-sm text-gray-600">
            로그인 페이지로 이동 중...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
        <h2 className="text-xl font-semibold text-gray-900">로그인 중...</h2>
        <p className="mt-2 text-sm text-gray-600">잠시만 기다려주세요</p>
      </div>
    </div>
  );
}
